<script lang="ts">
  import BottomSheet from './BottomSheet.svelte';
  import type { Item, ProbMode, SpecialRole } from '../lib/types';

  interface Props {
    open: boolean;
    item: Item | null;
    /** 自身を除く他の固定%項目の合計 (合計 100% 超チェック用) */
    otherFixedSum: number;
    /** ON 項目数 (重みドロップダウンの上限算出用) */
    enabledCount: number;
    /** デフォルト (パレット既定) 色 */
    defaultColor: string;
    onsave: (updated: Item) => void;
    onclose: () => void;
  }

  let {
    open,
    item,
    otherFixedSum,
    enabledCount,
    defaultColor,
    onsave,
    onclose,
  }: Props = $props();

  let probMode = $state<ProbMode>('default');
  let probValue = $state<number>(1);
  let splitCount = $state<number>(1);
  let useCustomColor = $state(false);
  let customColorValue = $state('#888888');
  let probError = $state<string>('');
  let specialRole = $state<SpecialRole | undefined>(undefined);

  $effect(() => {
    if (open && item) {
      probMode = item.probMode;
      probValue =
        item.probValue ?? (item.probMode === 'fixed' ? 50 : 1);
      splitCount = item.splitCount;
      useCustomColor = !!item.customColor;
      customColorValue = item.customColor || defaultColor || '#888888';
      probError = '';
      specialRole = item.specialRole;
    }
  });

  // 重みドロップダウンの選択肢: 0.25, 0.5, 0.75, 1, 1.5, 2, ... ON項目数まで 0.5 刻み
  const weightOptions = $derived.by(() => {
    const opts: number[] = [0.25, 0.5, 0.75];
    const maxWeight = Math.max(1, enabledCount);
    for (let v = 1; v <= maxWeight; v += 0.5) {
      opts.push(v);
    }
    return opts;
  });

  function selectMode(m: ProbMode): void {
    probMode = m;
    probError = '';
    if (m === 'fixed' && (probValue <= 0 || probValue >= 100)) {
      probValue = 50;
    } else if (m === 'weight' && probValue < 0.25) {
      probValue = 1;
    }
  }

  function save(): void {
    if (!item) return;

    if (probMode === 'fixed') {
      if (!Number.isFinite(probValue) || probValue <= 0 || probValue >= 100) {
        probError = '0% より大きく 100% 未満で指定してください';
        return;
      }
      if (otherFixedSum + probValue > 100) {
        probError = `他の固定%との合計が 100% を超えます (他: ${otherFixedSum}%)`;
        return;
      }
    } else if (probMode === 'weight') {
      if (!Number.isFinite(probValue) || probValue < 0.25) {
        probError = '0.25 以上で指定してください';
        return;
      }
    }

    const sc = Math.max(1, Math.min(10, Math.round(splitCount)));

    const updated: Item = {
      ...item,
      probMode,
      probValue: probMode === 'default' ? undefined : probValue,
      splitCount: sc,
      customColor: useCustomColor ? customColorValue : undefined,
      specialRole,
    };
    onsave(updated);
    onclose();
  }
</script>

<BottomSheet {open} title="項目詳細" {onclose}>
  {#if item}
    <p class="item-name">{item.text}</p>

    <section class="field">
      <div class="field-label" id="prob-mode-label">確率モード</div>
      <div class="seg-group" role="radiogroup" aria-labelledby="prob-mode-label">
        <button
          class:active={probMode === 'default'}
          onclick={() => selectMode('default')}
          role="radio"
          aria-checked={probMode === 'default'}
        >
          デフォルト
        </button>
        <button
          class:active={probMode === 'fixed'}
          onclick={() => selectMode('fixed')}
          role="radio"
          aria-checked={probMode === 'fixed'}
        >
          固定 %
        </button>
        <button
          class:active={probMode === 'weight'}
          onclick={() => selectMode('weight')}
          role="radio"
          aria-checked={probMode === 'weight'}
        >
          重み
        </button>
      </div>
    </section>

    {#if probMode === 'fixed'}
      <section class="field">
        <label class="field-label" for="prob-fixed">確率 (%)</label>
        <input
          id="prob-fixed"
          type="number"
          bind:value={probValue}
          min="0.5"
          max="99.5"
          step="0.5"
          inputmode="decimal"
        />
        <p class="field-hint">0% より大きく 100% 未満（他の固定% 合計: {otherFixedSum}%）</p>
      </section>
    {:else if probMode === 'weight'}
      <section class="field">
        <label class="field-label" for="prob-weight">重み</label>
        <select id="prob-weight" bind:value={probValue}>
          {#each weightOptions as w (w)}
            <option value={w}>{w}</option>
          {/each}
        </select>
        <p class="field-hint">他の項目との比率で配分されます (大きいほど当選しやすい)</p>
      </section>
    {:else}
      <section class="field">
        <p class="field-hint">他のデフォルト項目と均等に配分されます。</p>
      </section>
    {/if}

    <section class="field">
      <label class="field-label" for="split-count">分割数: {splitCount}</label>
      <input
        id="split-count"
        type="range"
        bind:value={splitCount}
        min="1"
        max="10"
        step="1"
      />
    </section>

    <section class="field">
      <div class="field-label" id="role-label">特殊演出ロール</div>
      <div class="seg-group" role="radiogroup" aria-labelledby="role-label">
        <button
          class:active={specialRole === undefined}
          onclick={() => { specialRole = undefined; }}
          role="radio"
          aria-checked={specialRole === undefined}
        >通常</button>
        <button
          class="role-target"
          class:active={specialRole === 'target'}
          onclick={() => { specialRole = specialRole === 'target' ? undefined : 'target'; }}
          role="radio"
          aria-checked={specialRole === 'target'}
        >⭐ 当てたい</button>
        <button
          class="role-avoid"
          class:active={specialRole === 'avoid'}
          onclick={() => { specialRole = specialRole === 'avoid' ? undefined : 'avoid'; }}
          role="radio"
          aria-checked={specialRole === 'avoid'}
        >❌ 当てたくない</button>
      </div>
      <p class="field-hint">設定すると、当選時に特殊演出が発生します (演出設定で ON にしている場合)</p>
    </section>

    <section class="field">
      <div class="field-label">セグメント色</div>
      <div class="color-row">
        <label class="color-toggle">
          <input type="checkbox" bind:checked={useCustomColor} />
          <span>カスタム色</span>
        </label>
        <input
          class="color-picker"
          type="color"
          bind:value={customColorValue}
          disabled={!useCustomColor}
          aria-label="セグメント色"
        />
        <span
          class="color-preview"
          style:background={useCustomColor ? customColorValue : defaultColor}
          aria-hidden="true"
        ></span>
      </div>
    </section>

    {#if probError}
      <p class="error" role="alert">{probError}</p>
    {/if}

    <div class="actions">
      <button class="cancel" onclick={onclose}>キャンセル</button>
      <button class="save" onclick={save}>保存</button>
    </div>
  {/if}
</BottomSheet>

<style>
  .item-name {
    margin: 0 0 var(--space-lg);
    font-size: var(--font-size-lg);
    font-weight: 600;
    word-break: break-word;
    padding: var(--space-sm) var(--space-md);
    background: var(--color-bg);
    border-radius: var(--radius-sm);
  }

  .field {
    margin-bottom: var(--space-lg);
  }

  .field-label {
    display: block;
    font-size: var(--font-size-sm);
    font-weight: 600;
    color: var(--color-fg-muted);
    margin-bottom: var(--space-sm);
  }

  .field-hint {
    margin: 6px 0 0;
    font-size: 12px;
    color: var(--color-fg-muted);
    line-height: 1.4;
  }

  .seg-group {
    display: flex;
    gap: 3px;
    background: var(--color-border);
    padding: 3px;
    border-radius: var(--radius-sm);
  }

  .seg-group button {
    flex: 1;
    padding: 9px 6px;
    font-size: var(--font-size-sm);
    font-weight: 500;
    border-radius: 6px;
    color: var(--color-fg-muted);
    transition: background 0.12s;
  }

  .seg-group button.active {
    background: var(--color-bg);
    color: var(--color-fg);
    box-shadow: var(--shadow-sm);
    font-weight: 700;
  }

  .seg-group button.role-target.active {
    background: #fffbe6;
    color: #b45309;
  }

  .seg-group button.role-avoid.active {
    background: #fef2f2;
    color: #b91c1c;
  }

  input[type='number'],
  select {
    width: 100%;
    padding: 11px var(--space-md);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-sm);
    background: var(--color-bg);
    color: var(--color-fg);
    font-size: var(--font-size-base);
  }

  input[type='number']:focus,
  select:focus {
    outline: 2px solid var(--color-accent);
    outline-offset: -1px;
  }

  input[type='range'] {
    width: 100%;
    accent-color: var(--color-accent);
  }

  .color-row {
    display: flex;
    align-items: center;
    gap: var(--space-md);
  }
  .color-toggle {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: var(--font-size-sm);
    flex: 1;
    cursor: pointer;
  }
  .color-toggle input[type='checkbox'] {
    width: 18px;
    height: 18px;
    accent-color: var(--color-accent);
  }
  .color-picker {
    width: 44px;
    height: 36px;
    padding: 0;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-sm);
    background: var(--color-bg);
    cursor: pointer;
  }
  .color-picker:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  .color-preview {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: 2px solid var(--color-border);
    flex-shrink: 0;
  }

  .error {
    color: var(--color-accent);
    font-size: var(--font-size-sm);
    margin: var(--space-sm) 0;
    padding: var(--space-sm);
    background: rgba(233, 69, 96, 0.1);
    border-radius: var(--radius-sm);
  }

  .actions {
    display: flex;
    gap: var(--space-sm);
    margin-top: var(--space-lg);
  }

  .actions button {
    flex: 1;
    padding: 12px;
    border-radius: var(--radius-sm);
    font-weight: 600;
    font-size: var(--font-size-base);
  }

  .cancel {
    background: var(--color-border);
    color: var(--color-fg);
  }

  .save {
    background: var(--color-accent);
    color: var(--color-accent-fg);
  }

  .save:active {
    transform: scale(0.98);
  }
</style>
